import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import {
    ContractListEntityType,
    IContractEntityInfo,
    IContractListData,
} from 'src/app/domain/registration/interfaces/contract-list.interfaces';
import { IContract } from 'src/app/domain/registration/models/IContract';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContractsService } from '../../../crud-service';
import { ContractViewDialogComponent } from './contract-view-dialog/contract-view-dialog.component';
import { ContractsDialogComponent } from '../contracts-dialog.component';
import { WhatsappSelectionDialogComponent } from './whatsapp-selection-dialog/whatsapp-selection-dialog.component';
import { CustomerContractGenerationService } from '../../../services/customer-contract-generation.service';
import { PartnerContractGenerateComponent } from '../../../../partners/components/contract-generate/contract-generate.component';

interface WhatsappRecipient {
    id: string;
    name: string;
    role: string;
    email: string;
    phone: string | null;
    type: ContractListEntityType | 'signatario';
    signUrl?: string;
}

@Component({
    selector: 'app-contracts-list-dialog',
    templateUrl: './contracts-list-dialog.component.html',
    styleUrls: ['./contracts-list-dialog.component.sass']
})
export class ContractsListDialogComponent implements OnInit, OnDestroy {
    private readonly CACHE_DURATION = 30000;
    private readonly AUTO_REFRESH_INTERVAL = 60000;

    private contractsSubject = new BehaviorSubject<IContract[]>([]);
    private subscriptions: Subscription[] = [];
    private lastUpdateTime: number = 0;

    contracts$ = this.contractsSubject.asObservable();
    entityType: ContractListEntityType;
    entityId: string = '';
    entityInfo?: IContractEntityInfo;
    title: string;
    isSingleContractMode = false;

    isLoading = false;
    isDownloading = false;
    isBackgroundRefreshing = false;
    
    showNewContractButton = true;
    showUpdateAllButton = true;

    constructor(
        public dialogRef: MatDialogRef<ContractsListDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IContractListData,
        private contractsService: ContractsService,
        private toastService: ToastService,
        private dialog: MatDialog,
        private clipboard: Clipboard,
        private customerContractGenerationService: CustomerContractGenerationService
    ) {
        if (data.singleContract) {
            this.isSingleContractMode = true;
            this.title = 'Detalhes do Contrato';
            this.entityType = data.type || 'customer';
            this.contractsSubject.next([data.singleContract]);
            this.showNewContractButton = false;
            this.showUpdateAllButton = true;
            return;
        }

        this.entityType = data.type;
        this.entityId = this.extractEntityId(data);
        this.entityInfo = this.buildEntityInfo(data);
        this.title = this.buildTitle();
        this.showNewContractButton = data.showNewContractButton !== undefined ? data.showNewContractButton : true;
        this.showUpdateAllButton = data.showUpdateAllButton !== undefined ? data.showUpdateAllButton : true;

        if (!this.entityId) {
            this.toastService.showError('Erro ao abrir modal: ID da entidade não encontrado.');
            this.dialogRef.close();
            return;
        }

        this.subscriptions.push(
            this.dialogRef.afterOpened().subscribe(() => this.updateAllContractsStatus())
        );
    }

    ngOnInit(): void { }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private extractEntityId(data: IContractListData): string {
        if (data.entityInfo?.id) return data.entityInfo.id;
        if (this.entityType === 'customer' && data.customerId) return data.customerId;
        if (this.entityType === 'partner' && data.partnerId) return data.partnerId;
        return '';
    }

    private buildEntityInfo(data: IContractListData): IContractEntityInfo {
        return data.entityInfo || {
            id: this.entityId,
            name: data.type === 'customer' ? data.customerName : data.partnerName,
            document: data.type === 'customer' ? data.customerDocument : data.partnerDocument,
            email: data.type === 'customer' ? data.customerEmail : data.partnerEmail,
            phone: data.type === 'customer' ? data.customerPhone : data.partnerPhone
        };
    }

    private buildTitle(): string {
        const entityTypeName = this.entityType === 'customer' ? 'Cliente' : 'Parceiro';
        const entityName = this.entityInfo?.name || 'Desconhecido';
        return `Contratos do ${entityTypeName}: ${entityName}`;
    }

    private getContracts(): Observable<IContract[]> {
        this.isLoading = true;

        const observable = this.entityType === 'customer'
            ? this.contractsService.getContractsByCustomer(this.entityId)
            : this.contractsService.getContractsByPartner(this.entityId);

        return observable.pipe(
            tap(() => this.isLoading = false),
            catchError(error => {
                this.isLoading = false;
                this.toastService.showError(`Erro ao buscar contratos do ${this.entityType}`);
                return of([]);
            })
        );
    }

    refreshContracts(forceRefresh = false): void {
        if (!forceRefresh && this.hasUpdatedRecently()) return;

        this.lastUpdateTime = Date.now();
        this.isLoading = true;

        const sub = this.getContracts().subscribe({
            next: (contracts: IContract[]) => {
                this.contractsSubject.next(contracts);
                this.isLoading = false;
            },
            error: () => this.isLoading = false
        });

        this.subscriptions.push(sub);
    }

    private hasUpdatedRecently(): boolean {
        return !!this.lastUpdateTime && (Date.now() - this.lastUpdateTime) < this.CACHE_DURATION;
    }

    updateAllContractsStatus(): void {
        if (this.isSingleContractMode) {
            this.toastService.showInfo('O contrato será atualizado automaticamente.');
            return;
        }

        this.isLoading = true;

        const sub = this.contractsService.updateAllContractsStatus(this.entityId, this.entityType)
            .subscribe({
                next: () => {
                    this.lastUpdateTime = 0;
                    this.refreshContracts();
                    this.isLoading = false;
                },
                error: () => {
                    this.toastService.showError('Não foi possível atualizar o status dos contratos.');
                    this.refreshContracts(true);
                    this.isLoading = false;
                }
            });

        this.subscriptions.push(sub);
    }

    backgroundRefresh(): void {
        if (this.isBackgroundRefreshing) return;

        this.isBackgroundRefreshing = true;
        this.isLoading = true;

        const sub = this.contractsService.updateAllContractsStatus(this.entityId, this.entityType).pipe(
            switchMap(() => this.getContracts())
        ).subscribe({
            next: (contracts: IContract[]) => {
                this.contractsSubject.next(contracts);
                this.isBackgroundRefreshing = false;
                this.isLoading = false;
            },
            error: () => {
                this.refreshContracts(true);
                this.isBackgroundRefreshing = false;
                this.isLoading = false;
            }
        });

        this.subscriptions.push(sub);
    }

    viewContract(contractId: string): void {
        const contract = this.contractsSubject.value.find(c => c.id === contractId);
        if (!contract) {
            this.toastService.showError('Contrato não encontrado.');
            return;
        }

        this.isLoading = true;

        this.contractsService.forceRefreshStatus(contractId, this.entityType).subscribe({
            next: (updatedContract) => {
                this.isLoading = false;
                this.updateContractInList(updatedContract);
                this.openContractViewModal(contractId, updatedContract.status);
            },
            error: () => {
                this.isLoading = false;
                this.toastService.showWarn('Erro ao atualizar status, abrindo contrato com status anterior');
                this.openContractViewModal(contractId, contract.status);
            }
        });
    }

    private updateContractInList(updatedContract: any): void {
        const contracts = this.contractsSubject.value;
        const index = contracts.findIndex(c => c.id === updatedContract.id);

        if (index !== -1) {
            contracts[index] = updatedContract;
            this.contractsSubject.next([...contracts]);
        }
    }

    private openContractViewModal(contractId: string, status: string): void {
        const isSigned = status === 'signed' || status === 'archived';

        this.dialog.open(ContractViewDialogComponent, {
            width: '80%',
            height: '90%',
            data: {
                contractId,
                contractType: this.entityType,
                useZapSign: isSigned,
                previewMode: !isSigned
            }
        });
    }

    downloadContract(contractId: string): void {
        if (this.isDownloading) return;

        this.isDownloading = true;
        this.isLoading = true;
        this.toastService.showInfo('Preparando download...');

        const sub = this.contractsService.downloadContract(contractId, this.entityType).subscribe({
            next: (blob: Blob) => {
                this.processDownload(blob, contractId);
                this.isDownloading = false;
                this.isLoading = false;
            },
            error: () => {
                this.toastService.showError('Erro ao baixar contrato. Tente novamente.');
                this.isDownloading = false;
                this.isLoading = false;
            }
        });

        this.subscriptions.push(sub);
    }

    private processDownload(blob: Blob, contractId: string): void {
        if (!blob || blob.size === 0) {
            this.toastService.showError('PDF vazio ou inválido');
            return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Contrato_${this.entityType}_${contractId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.toastService.showSuccess('Download iniciado com sucesso');
    }

    removeContract(contractId: string): void {
        if (!confirm('Tem certeza que deseja remover este contrato?')) return;

        this.isLoading = true;

        this.contractsService.forceRefreshStatus(contractId, this.entityType).subscribe({
            next: (contract) => {
                if (this.canCancelOnZapSign(contract)) {
                    this.cancelContractOnZapSign(contractId);
                } else {
                    this.removeContractLocally(contractId);
                }
            },
            error: () => {
                this.toastService.showError('Erro ao validar contrato. Não foi possível remover.');
                this.isLoading = false;
            }
        });
    }

    private canCancelOnZapSign(contract: any): boolean {
        const validStatuses = ['processing', 'waiting_for_signatories', 'waiting_for_signatures', 'pending', 'link_opened', 'editing'];
        return !!contract?.zapSignToken && !!contract?.status && validStatuses.includes(contract.status);
    }

    private cancelContractOnZapSign(contractId: string): void {
        this.toastService.showInfo('Cancelando contrato na ZapSign...');

        this.contractsService.removeContract(contractId, this.entityType).subscribe({
            next: () => {
                this.toastService.showSuccess('Contrato removido/cancelado com sucesso.');
                this.refreshContracts(true);
                this.isLoading = false;
            },
            error: () => {
                this.toastService.showError('Erro ao cancelar na ZapSign.');
                this.refreshContracts(true);
                this.isLoading = false;
            }
        });
    }

    private removeContractLocally(contractId: string): void {
        this.contractsService.removeContract(contractId, this.entityType).subscribe({
            next: () => {
                this.toastService.showSuccess('Contrato removido com sucesso.');
                this.refreshContracts(true);
                this.isLoading = false;
            },
            error: () => {
                this.toastService.showError('Erro ao remover contrato.');
                this.isLoading = false;
            }
        });
    }

    resendContractEmails(contractId: string): void {
        this.isLoading = true;

        this.contractsService.forceRefreshStatus(contractId, this.entityType).subscribe({
            next: (contract) => {
                if (this.canResendEmails(contract)) {
                    this.executeEmailResend(contractId, contract);
                } else {
                    this.showResendEmailError(contract);
                    this.isLoading = false;
                }
            },
            error: () => {
                this.toastService.showError('Erro ao verificar status do contrato.');
                this.isLoading = false;
            }
        });
    }

    private canResendEmails(contract: any): boolean {
        if (!contract?.zapSignToken) return false;

        const validStatuses = ['waiting_for_signatories', 'waiting_for_signatures', 'link_opened', 'editing'];
        return validStatuses.includes(contract.status || '');
    }

    private showResendEmailError(contract: any): void {
        if (!contract?.zapSignToken) {
            this.toastService.showWarn('Contrato ainda não foi enviado para a ZapSign.');
        } else if (['processing', 'pending'].includes(contract.status)) {
            this.toastService.showWarn('Contrato ainda está sendo processado. Aguarde alguns minutos.');
        } else if (['signed', 'archived', 'canceled'].includes(contract.status || '')) {
            this.toastService.showWarn('Contrato já foi finalizado ou cancelado.');
        } else {
            this.toastService.showWarn(`Não é possível reenviar emails no status atual: ${this.getStatusLabel(contract.status || '', contract)}`);
        }
    }

    private executeEmailResend(contractId: string, contract: any): void {
        this.toastService.showInfo('Reenviando emails para signatários...');

        this.contractsService.resendContractEmails(contractId, this.entityType).subscribe({
            next: (response) => {
                this.isLoading = false;

                if (response?.success) {
                    this.toastService.showSuccess(`Emails reenviados com sucesso. ${response.message || ''}`);
                } else {
                    this.toastService.showWarn(`Emails não reenviados: ${response?.message || 'Verifique se há signatários pendentes.'}`);
                }

                this.refreshContracts(true);
            },
            error: () => {
                this.isLoading = false;
                this.toastService.showError('Erro ao reenviar emails de assinatura.');
            }
        });
    }

    generateNewContract(): void {
        const entityDataForModal = {
            ...this.entityInfo,
            type: this.entityType
        };

        // Para clientes, usar ContractsDialogComponent com services necessários
        if (this.entityType === 'customer') {
            const dialogRef = this.dialog.open(ContractsDialogComponent, {
                width: '80%',
                maxWidth: '95vw',
                maxHeight: '95vh',
                data: {
                    entity: entityDataForModal,
                    entityType: this.entityType,
                    entityService: this.customerContractGenerationService,
                    generationService: this.customerContractGenerationService
                }
            });

            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.refreshContracts(true);
                }
            });
        } 
        // Para parceiros, usar PartnerContractGenerateComponent
        else if (this.entityType === 'partner') {
            const dialogRef = this.dialog.open(PartnerContractGenerateComponent, {
                width: '80%',
                maxWidth: '95vw',
                maxHeight: '95vh',
                data: {
                    entity: entityDataForModal,
                    type: this.entityType
                }
            });

            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.refreshContracts(true);
                }
            });
        }
    }

    sendContractWhatsapp(contract: any): void {
        if (!contract) {
            this.toastService.showError('Contrato não encontrado');
            return;
        }

        this.isLoading = true;

        this.contractsService.forceRefreshStatus(contract.id, this.entityType).subscribe({
            next: (updatedContract) => {
                const recipients = this.buildWhatsappRecipients(updatedContract);
                this.isLoading = false;

                if (recipients.length === 0) {
                    this.toastService.showWarn('Nenhum signatário encontrado para este contrato.');
                    return;
                }

                this.openWhatsappSelectionDialog(contract.id, recipients);
            },
            error: () => {
                this.isLoading = false;
                this.toastService.showError('Erro ao buscar dados atualizados do contrato.');
            }
        });
    }

    private buildWhatsappRecipients(contract: any): WhatsappRecipient[] {
        const recipients: WhatsappRecipient[] = [];
        const addedEmails = new Set<string>();

        if (!contract.signatures || contract.signatures.length === 0) {
            return recipients;
        }

        contract.signatures.forEach((signer: any) => {
            const emailLower = signer.email?.toLowerCase();
            if (!emailLower || addedEmails.has(emailLower)) return;

            let phone = signer.whatsAppNumber || signer.cellPhone || signer.phone;

            if (!phone) {
                phone = this.findRelatedEntityPhone(contract, signer.email);
            }

            recipients.push({
                id: `signatario-${signer.id || emailLower}`,
                name: signer.name || signer.companyName || signer.tradingName || signer.role,
                role: signer.role || 'Signatário',
                email: signer.email,
                phone: phone,
                type: 'signatario',
                signUrl: signer.signUrl
            });

            addedEmails.add(emailLower);
        });

        return recipients;
    }

    private findRelatedEntityPhone(contract: any, email: string): string | null {
        if (!email) return null;

        const emailLower = email.toLowerCase();
        const entities = [
            contract.customer,
            contract.partner,
            contract.consultant,
            contract.representative,
            contract.counter
        ];

        for (const entity of entities) {
            if (entity && entity.email?.toLowerCase() === emailLower) {
                return entity.cellPhone || entity.phone || null;
            }
        }

        return null;
    }

    private openWhatsappSelectionDialog(contractId: string, recipients: WhatsappRecipient[]): void {
        const dialogRef = this.dialog.open(WhatsappSelectionDialogComponent, {
            width: '500px',
            data: { recipients, contractId }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.length > 0) {
                this.sendSelectedWhatsappMessages(contractId, result, recipients);
            }
        });
    }

    private sendSelectedWhatsappMessages(contractId: string, selectedRecipientIds: string[], allRecipients: WhatsappRecipient[]): void {
        this.isLoading = true;
        this.toastService.showInfo(`Preparando envio de WhatsApp para ${selectedRecipientIds.length} destinatário(s)...`);

        this.contractsService.getWhatsappLinks(contractId, this.entityType).subscribe({
            next: (zapSignData) => {
                this.isLoading = false;
                const messagesSent = this.sendWhatsappToRecipients(selectedRecipientIds, allRecipients, zapSignData);

                if (messagesSent > 0) {
                    this.toastService.showSuccess(`${messagesSent} mensagem(ns) de WhatsApp aberta(s).`);
                } else {
                    this.toastService.showWarn('Não foi possível gerar links de WhatsApp.');
                }
            },
            error: () => {
                this.isLoading = false;
                this.toastService.showError('Erro ao obter links para WhatsApp.');
            }
        });
    }

    private sendWhatsappToRecipients(selectedIds: string[], allRecipients: WhatsappRecipient[], zapSignData: any): number {
        let count = 0;

        selectedIds.forEach(recipientId => {
            const recipient = allRecipients.find(r => r.id === recipientId);
            if (!recipient || !recipient.phone) return;

            const signerData = zapSignData.signers?.find((s: any) =>
                s.email?.toLowerCase() === recipient.email?.toLowerCase() &&
                s.role?.toUpperCase() === recipient.role?.toUpperCase()
            );

            const link = this.buildWhatsappLink(recipient, signerData);

            if (link) {
                window.open(link, '_blank');
                count++;
            }
        });

        return count;
    }

    private buildWhatsappLink(recipient: WhatsappRecipient, signerData: any): string | null {
        if (signerData?.whatsappLink) {
            return signerData.whatsappLink;
        }

        const signUrl = signerData?.signUrl || recipient.signUrl;
        if (signUrl) {
            const message = `Olá ${recipient.name}! Você recebeu um contrato para assinatura. Acesse: ${signUrl}`;
            return this.generateWhatsAppLink(recipient.phone!, message);
        }

        const genericMessage = `Olá ${recipient.name}! Você recebeu um contrato para assinatura. Verifique seu email.`;
        return this.generateWhatsAppLink(recipient.phone!, genericMessage);
    }

    private generateWhatsAppLink(phone: string, message: string): string {
        let cleanPhone = phone.replace(/\D/g, '');
        if (!cleanPhone.startsWith('55')) {
            cleanPhone = '55' + cleanPhone;
        }
        return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    }

    copySignUrl(signUrl: string, signatureName: string): void {
        if (!signUrl) {
            this.toastService.showWarn(`Link de assinatura não disponível para ${signatureName}.`);
            return;
        }

        const success = this.clipboard.copy(signUrl);
        if (success) {
            this.toastService.showSuccess(`Link de assinatura de ${signatureName} copiado!`);
        } else {
            this.fallbackCopyToClipboard(signUrl, signatureName);
        }
    }

    private fallbackCopyToClipboard(text: string, signatureName: string): void {
        if (!navigator.clipboard) {
            this.toastService.showError('Navegador não suporta copiar para área de transferência.');
            return;
        }

        navigator.clipboard.writeText(text)
            .then(() => this.toastService.showSuccess(`Link de ${signatureName} copiado!`))
            .catch(() => this.toastService.showError('Falha ao copiar o link de assinatura.'));
    }

    getStatusClass(status: string, contract: IContract): string {
        if (contract?.localGeneration && status !== 'signed') {
            return 'status-local-fallback';
        }

        const statusClassMap: Record<string, string> = {
            'processing': 'status-processing',
            'waiting_for_signatories': 'status-waiting-signatories',
            'waiting_for_signatures': 'status-waiting-signatures',
            'link_opened': 'status-viewed',
            'signed': 'status-signed',
            'archived': 'status-archived',
            'canceled': 'status-canceled',
            'editing': 'status-editing'
        };

        return statusClassMap[status?.toLowerCase()] || 'status-default';
    }

    getStatusLabel(status: string, contract: IContract): string {
        if (contract?.localGeneration && status !== 'signed') {
            return 'NAO ENVIADO P/ ASSINATURA';
        }

        const statusMap: Record<string, string> = {
            'processing': 'Processando',
            'waiting_for_signatories': 'Aguard. Signatários',
            'waiting_for_signatures': 'Aguard. Assinaturas',
            'link_opened': 'Visualizado',
            'signed': 'Assinado',
            'archived': 'Arquivado',
            'canceled': 'Cancelado',
            'editing': 'Editando'
        };

        return statusMap[status?.toLowerCase()] || 'Desconhecido';
    }

    close(): void {
        this.dialogRef.close();
    }
}
